import * as React from 'react';

import {
  Button,
  cn,
  ConfirmPopoverButton,
  DataGridColumn,
  LocalizedDate,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucideHistory, LucideTrash2, LucideX } from 'lucide-react';

import { $pendingChanges } from '../../../_state/variablesList.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { VariableRecord } from '../../../types/pricing';

const valueFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

type Props = {
  onEdit?: (row: VariableRecord) => void;
  onDelete?: (id: string) => void;
  onHistory?: (row: VariableRecord) => void;
  onPendingChange?: (
    variableId: string,
    variableName: string,
    oldValue: number | null,
    newValue: number | null,
  ) => void;
  onClearValue?: (
    variableId: string,
    variableName: string,
    oldValue: number | null,
  ) => void;
};

//
// ⚛️ ⚛️ ⚛️ VALUE CELL ⚛️ ⚛️ ⚛️
//
const ValueCell = ({
  row,
  onPendingChange,
  onClearValue,
}: {
  row: VariableRecord;
  onPendingChange?: Props['onPendingChange'];
  onClearValue?: Props['onClearValue'];
}) => {
  const pendingChanges = useAtomValue($pendingChanges);
  const pending = pendingChanges.get(row.id);
  const resolvedValue = pending ? pending.newValue : row.value;
  const hasPending = !!pending;

  const [isFocused, setIsFocused] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(
    resolvedValue != null ? String(resolvedValue) : '',
  );

  React.useEffect(() => {
    if (isFocused) return;
    const val = pending ? pending.newValue : row.value;
    setLocalValue(val != null ? String(val) : '');
  }, [pending, row.value, isFocused]);

  if (!onPendingChange) {
    return (
      <div className="text-right">
        {row.value != null ? valueFormatter.format(row.value) : '—'}
      </div>
    );
  }

  const showClear = resolvedValue != null && onClearValue;
  const displayText =
    isFocused || localValue === ''
      ? localValue
      : resolvedValue != null
        ? valueFormatter.format(resolvedValue)
        : '';

  return (
    <div
      className={cn(
        'flex items-center gap-1',
        hasPending && 'rounded bg-warning/10',
      )}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="text"
        inputMode="decimal"
        className="w-full bg-transparent px-2 py-1 text-right text-sm outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
        value={displayText}
        onFocus={() => {
          setIsFocused(true);
          setLocalValue(resolvedValue != null ? String(resolvedValue) : '');
        }}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={() => {
          setIsFocused(false);
          if (localValue === '') {
            if (row.value != null) {
              onClearValue?.(row.id, row.name, row.value);
            }
            return;
          }
          const val = Number(localValue);
          if (!isNaN(val) && val !== row.value) {
            onPendingChange(row.id, row.name, row.value, val);
          }
        }}
      />
      {showClear && (
        <button
          className="flex size-5 shrink-0 items-center justify-center rounded text-neutral/40 hover:text-neutral"
          onClick={() => onClearValue(row.id, row.name, row.value)}
        >
          <LucideX className="size-3" />
        </button>
      )}
    </div>
  );
};

export const useVariableColumns = ({
  onEdit,
  onDelete,
  onHistory,
  onPendingChange,
  onClearValue,
}: Props = {}) => {
  const t = useTranslations();

  const columns: DataGridColumn<VariableRecord>[] = React.useMemo(
    () => [
      {
        key: 'name',
        header: t.pricing.variables.table.columns.name,
        sortable: true,
        width: 200,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center text-left">{row.name}</div>
        ),
      },
      {
        key: 'description',
        header: t.pricing.variables.table.columns.description,
        width: 300,
        cell: ({ row }) => (
          <div className="truncate text-left text-neutral/70">
            {row.description ?? '-'}
          </div>
        ),
      },
      {
        key: 'value',
        header: t.pricing.variables.table.columns.value,
        width: 150,
        cell: ({ row }) => (
          <ValueCell
            row={row}
            onPendingChange={onPendingChange}
            onClearValue={onClearValue}
          />
        ),
      },
      {
        key: 'updatedBy',
        header: t.pricing.variables.table.columns.updatedBy,
        width: 140,
        cell: ({ row }) => (
          <div className="text-left text-neutral/70">
            {row.updatedByUser?.name ?? '—'}
          </div>
        ),
      },
      {
        key: 'createdAt',
        header: t.pricing.variables.table.columns.created,
        width: 120,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.timestamps.createdAt} />
          </div>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: 96,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              icon={<LucideHistory size={14} />}
              onPress={() => onHistory?.(row)}
              aria-label={t.pricing.variables.history.title}
            />
            <ConfirmPopoverButton
              onConfirm={() => onDelete?.(row.id)}
              message={t.pricing.variables.deleteConfirm.message(row.name)}
              confirmLabel={t.common.actions.delete}
              cancelLabel={t.common.actions.cancel}
              renderTrigger={(props) => (
                <Button
                  {...props}
                  variant="ghost"
                  color="failure"
                  icon={<LucideTrash2 size={14} />}
                />
              )}
            />
          </div>
        ),
      },
    ],
    [t, onEdit, onDelete, onHistory, onPendingChange, onClearValue],
  );

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((col) => [col.key, true])),
    [columns],
  );

  return { columns, columnVisibilityState };
};
